import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Box, HStack, Text, useDisclosure } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as Switch } from 'assets/icons/switch.svg';
import {
  useExchange,
  useExchangeApprove,
  usePairs,
  useRate,
  useSwapAvailableDestinations,
  useSwapAvailableSources,
} from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';
import { ExchangeResponse, TransactionMethodKey, Wallet } from 'types';

import {
  Button,
  ExchangeWalletSelect,
  IconButton,
  Input,
  PercentSelect,
  ServiceFormCard,
  Spinner,
} from 'components';
import { fixDecimal, yup } from 'utils';

import ApproveModal from '../ApproveModal';

type PayloadType = {
  source_wallet: string;
  destination_wallet: string;
  amount: number;
};

const SimpleExchange: React.FC = () => {
  const { t } = useTranslation('component.exchange.simple');
  const { open, onOpen, onClose } = useDisclosure();

  const decimal: Record<string, 2 | 5> = { CRYPTO: 5, FIAT: 2 };
  const [searchparams, setSearchparams] = useSearchParams();

  const [exchangeDetail, setExchangeDetail] =
    React.useState<ExchangeResponse | null>(null);
  const [selected, setSelected] = React.useState<{
    source: Wallet | undefined;
    destination: Wallet | undefined;
  }>({ source: undefined, destination: undefined });
  const [pairsQuery, setPairsQuery] = React.useState<{
    first_currency: string | undefined;
    second_currency: string | undefined;
  }>({ first_currency: undefined, second_currency: undefined });

  const {
    mutateAsync: getAvailableSources,
    data: availableSources,
    isLoading: sourceLoading,
  } = useSwapAvailableSources();

  const {
    mutateAsync: getAvailableDestinations,
    data: availableDestinations,
    isLoading: destinationLoading,
  } = useSwapAvailableDestinations();

  const {
    refetch,
    data: pairs,
    remove: removePairs,
  } = usePairs({
    enabled: false,
    query: pairsQuery,
  });

  const {
    mutateAsync: fetchRate,
    data: rate,
    isLoading: rateLoading,
  } = useRate();

  const { mutateAsync: submit, isPending: submitLoading } = useExchange();
  const { mutateAsync: approve, isPending: approveLoading } =
    useExchangeApprove();

  const formRequirements = {
    yup: yup.object().shape({
      source_wallet: yup.string().label(t('form.source_wallet')).required(),
      destination_wallet: yup
        .string()
        .label(t('form.destination_wallet'))
        .required(),
      amount: yup.number().label(t('form.amount')).required(),
    }),
    schema: {
      source_wallet: t('form.source_wallet'),
      destination_wallet: t('form.destination_wallet'),
      amount: t('form.amount'),
    },
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
    resetField,
  } = useForm({
    resolver: yupResolver(formRequirements.yup),
    defaultValues: {},
  });

  const onSubmit = async (data: PayloadType) => {
    if (rate) {
      submit({
        ...data,
        destination_amount: rate?.rate * data.amount,
        rate: rate?.rate,
      }).then((res) => {
        if (res.id) {
          setExchangeDetail(res);
          onOpen();
        }
      });
    }
  };

  const formProps = useInputAttributes(register, errors, {
    amount: formRequirements.schema.amount,
  });

  const getDestinationListHandler = ({
    source_wallet,
  }: {
    source_wallet: string;
  }) =>
    getAvailableDestinations({ source_wallet }).then((res) => {
      setValue('destination_wallet', res[0].id);
      setSelected((prev) => ({ ...prev, destination: res[0] }));
    });

  const getSourceListHandler = () => {
    getAvailableSources({}).then((sourcesResult) => {
      if (sourcesResult.length > 0) {
        setSelected((prev) => ({
          ...prev,
          source:
            sourcesResult.find(
              (item) => item.id === searchparams.get('wallet') || ''
            ) || sourcesResult[0],
        }));
        getDestinationListHandler({
          source_wallet: searchparams.get('wallet') || sourcesResult[0].id,
        });
      }
    });
  };

  React.useEffect(() => {
    getSourceListHandler();
  }, []);

  React.useEffect(() => {
    setValue(
      'source_wallet',
      searchparams.get('wallet') || availableSources?.[0].id || ''
    );
  }, [availableSources]);

  React.useEffect(() => {
    if (selected?.source?.id && selected?.destination?.id) {
      setPairsQuery({
        first_currency: selected?.source?.currency.id,
        second_currency: selected?.destination?.currency.id,
      });
      setTimeout(() => {
        refetch();
      }, 500);
    }
  }, [selected?.source?.id, selected?.destination?.id]);

  React.useEffect(() => {
    if (
      selected?.source?.currency &&
      selected?.destination?.currency &&
      pairs?.list?.[0]?.transaction_methods?.[0].key
    ) {
      fetchRate({
        source_symbol: selected?.source?.currency.symbol,
        destination_symbol: selected?.destination?.currency.symbol,
        transaction_method_key: pairs?.list?.[0]?.transaction_methods?.[0]
          .key as TransactionMethodKey,
      });
    }
  }, [
    selected?.source?.id,
    selected?.destination?.id,
    pairs?.list?.[0]?.transaction_methods?.[0].key,
  ]);

  const sourceWalletAvailableAmount =
    (selected?.source?.amount || 0) - (selected?.source?.frozen_amount || 0);
  const destinationWalletAvailableAmount =
    (selected?.destination?.amount || 0) -
    (selected?.destination?.frozen_amount || 0);

  return (
    <Box w="100%">
      <ServiceFormCard>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            display="flex"
            flexDir={{ base: 'column', xl: 'row' }}
            w="100%"
            gap={{ base: 5, xl: 8 }}
            alignItems={{ base: 'stretch', xl: 'flex-start' }}
          >
            <Box
              display="flex"
              flexDir="column"
              w={{ base: '100%', xl: '50%' }}
              gap={3}
            >
              <Box w="100%" pos="relative">
                <Controller
                  name="source_wallet"
                  control={control}
                  render={({ field }) => (
                    <ExchangeWalletSelect
                      label={t('form.source_wallet')}
                      error={errors.source_wallet?.message}
                      wallets={availableSources || []}
                      value={field.value || ''}
                      isLoading={sourceLoading}
                      placeholder={t('selectWallet')}
                      onChange={(walletId) => {
                        field.onChange(walletId);
                        if (walletId) {
                          setSelected((prev) => ({
                            ...prev,
                            source: availableSources?.find(
                              (item) => walletId === item.id
                            ) as Wallet,
                          }));
                          getDestinationListHandler({
                            source_wallet: walletId,
                          });
                        } else {
                          setSelected((prev) => ({
                            ...prev,
                            source: undefined,
                          }));
                        }
                        setSelected((prev) => ({
                          ...prev,
                          destination: undefined,
                        }));
                        resetField('amount');
                        resetField('destination_wallet');
                        searchparams.delete('wallet');
                        setSearchparams(searchparams, { replace: true });
                      }}
                    />
                  )}
                />
                <Text fontSize="sm" color="body2" textAlign="end" mt={1}>
                  {selected.source
                    ? `${fixDecimal({
                        value: sourceWalletAvailableAmount,
                        fractionDigits: decimal[selected?.source?.type],
                      })} ${selected?.source?.currency.symbol}`
                    : ''}
                </Text>
              </Box>
              <Box w="100%">
                <Input
                  {...formProps.amount}
                  label=""
                  type="number"
                  min="0"
                  max="1000000000"
                  step="0.00001"
                  bg="secondary"
                  borderWidth="1px"
                  borderColor="rgba(255,255,255,0.08)"
                  borderRadius="lg"
                  py={20}
                  px={10}
                  fontSize="4xl"
                  fontWeight="500"
                  color="body"
                  _placeholder={{ color: 'body2' }}
                />
              </Box>
              <Box w="100%" pt={2}>
                <PercentSelect
                  list={[25, 50, 75, 100]}
                  onPercent={(item) =>
                    selected.source
                      ? setValue(
                          'amount',
                          parseFloat(
                            fixDecimal({
                              value: (sourceWalletAvailableAmount * item) / 100,
                              fractionDigits: decimal[selected?.source?.type],
                            })
                          )
                        )
                      : ''
                  }
                />
              </Box>
            </Box>
            <IconButton
              ariaLabel="menu"
              icon={Switch}
              alignSelf="center"
              mt={{ base: 0, xl: 24 }}
              size="md"
              borderRadius="lg"
              _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
              onClick={() => {
                setValue(
                  'amount',
                  parseFloat(((rate?.rate || 0) * watch('amount')).toFixed(5))
                );
                setValue('source_wallet', selected.destination?.id || '');
                setValue('destination_wallet', selected.source?.id || '');
                setSelected((prev) => ({
                  destination: prev.source,
                  source: prev.destination,
                }));
              }}
            />
            <Box
              display="flex"
              flexDir="column"
              w={{ base: '100%', xl: '50%' }}
              gap={3}
            >
              <Box w="100%" pos="relative">
                <Controller
                  name="destination_wallet"
                  control={control}
                  render={({ field }) => (
                    <ExchangeWalletSelect
                      label={t('form.destination_wallet')}
                      error={errors.destination_wallet?.message}
                      wallets={availableDestinations || []}
                      value={field.value || ''}
                      isLoading={destinationLoading}
                      placeholder={t('selectWallet')}
                      onChange={(walletId) => {
                        field.onChange(walletId);
                        if (walletId) {
                          removePairs();
                          setSelected((prev) => ({
                            ...prev,
                            destination: availableDestinations?.find(
                              (item) => item.id === walletId
                            ) as Wallet,
                          }));
                        } else {
                          setSelected((prev) => ({
                            ...prev,
                            destination: undefined,
                          }));
                        }
                      }}
                    />
                  )}
                />
                <Text fontSize="sm" color="body2" textAlign="end" mt={1}>
                  {selected?.destination
                    ? `${fixDecimal({
                        value: destinationWalletAvailableAmount,
                        fractionDigits: decimal[selected?.destination?.type],
                      })} ${selected?.destination?.currency.symbol}`
                    : ''}
                </Text>
              </Box>
              <Box w="100%">
                <Input
                  label=""
                  name=""
                  bg="secondary"
                  borderWidth="1px"
                  borderColor="rgba(255,255,255,0.08)"
                  borderRadius="lg"
                  py={20}
                  px={10}
                  fontSize="4xl"
                  fontWeight="500"
                  color="body"
                  opacity={0.88}
                  _placeholder={{ color: 'body2' }}
                  disabled
                  value={
                    selected?.destination &&
                    watch('amount') &&
                    watch('amount') <= 100000000000 &&
                    rate
                      ? fixDecimal({
                          value: rate?.rate * watch('amount'),
                          fractionDigits: decimal[selected?.destination?.type],
                        })
                      : ''
                  }
                />
              </Box>
            </Box>
          </Box>
          <Box
            py={5}
            display="flex"
            flexDir={{ base: 'column', sm: 'row' }}
            alignItems={{ base: 'flex-start', sm: 'center' }}
            gap={{ base: 2, sm: 3 }}
            w="100%"
            borderTopWidth="1px"
            borderTopColor="rgba(255,255,255,0.08)"
            mt={2}
          >
            <Text fontSize="sm" color="body2" fontWeight="medium">
              {t('currentRate')}
            </Text>
            {selected?.source && selected?.destination && (
              <Box minW={0}>
                {rateLoading ? (
                  <Spinner size="sm" borderWidth="2px" />
                ) : (
                  <HStack gap={1} flexWrap="wrap">
                    <Text
                      fontSize="sm"
                      color="body"
                    >{`1 ${selected?.source?.currency.symbol} =`}</Text>
                    {rate?.rate && (
                      <Text
                        fontSize="sm"
                        color="body"
                        fontVariantNumeric="tabular-nums"
                      >
                        {`${fixDecimal({
                          value: rate?.rate,
                          fractionDigits: 5,
                        })} ${selected?.destination?.currency.symbol}`}
                      </Text>
                    )}
                  </HStack>
                )}
              </Box>
            )}
          </Box>
          <Box pt={5} w={{ base: '100%', md: '50%' }} maxW={{ md: '420px' }}>
            <Button
              w="100%"
              variant="filled"
              title={
                (
                  <Text>
                    {watch('amount') > sourceWalletAvailableAmount
                      ? t('insufficientBalance')
                      : t('exchangeSubmit')}
                  </Text>
                ) as unknown as string
              }
              type="submit"
              isDisabled={watch('amount') > sourceWalletAvailableAmount}
              isLoading={submitLoading}
            />
          </Box>
        </form>
      </ServiceFormCard>
      <ApproveModal
        data={{
          amount: watch('amount'),
          destination_amount: parseFloat(
            fixDecimal({
              value: (rate?.rate || 1) * watch('amount'),
              fractionDigits: decimal[selected?.destination?.type || 'CRYPTO'],
            })
          ),
          from: selected?.source?.currency.symbol || '',
          to: selected?.destination?.currency.symbol || '',
          rate: parseFloat(
            fixDecimal({ value: rate?.rate || 1, fractionDigits: 5 })
          ),
        }}
        onClose={onClose}
        isOpen={open}
        loading={approveLoading}
        onButton={() => {
          approve({ id: exchangeDetail?.id || '' }).then(() => {
            setSelected({ source: undefined, destination: undefined });
            setExchangeDetail(null);
            getSourceListHandler();
            reset();
          });
        }}
      />
    </Box>
  );
};

export default SimpleExchange;
